import { useId } from 'react';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { MESSAGE_MAX } from '@/lib/bot-service-hours';

// Mode + message editor for one out-of-hours scope: a radio pair (reply with the message /
// silent) above the message body with the server's placeholder chips and a rune counter.
// Controlled: onChange({ mode }) / onChange({ message }). `errors` carries the already
// path-resolved strings ({ mode, message }).

const MODE_OPTIONS = [
  {
    value: 'reply',
    label: 'Reply with message',
    hint: 'Patients get the message below and the thread is marked out of service for staff.',
  },
  {
    value: 'silent',
    label: "Silent — mark out of service, don't reply",
    hint: 'Nothing is sent; the thread is only marked out of service for staff to pick up.',
  },
];

/** Display-only chip strip of the server's {{token}} vocabulary. */
export function PlaceholderChips({ placeholders }) {
  if (!Array.isArray(placeholders) || placeholders.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {placeholders.map((name) => (
        <code
          key={name}
          className="rounded bg-background px-1.5 py-0.5 text-[11px] text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]"
        >{`{{${name}}}`}</code>
      ))}
    </div>
  );
}

export function OutOfHoursMessageEditor({
  mode,
  message,
  placeholders = [],
  errors = {},
  disabled = false,
  onChange,
  messagePlaceholder,
}) {
  const radioName = useId();
  const text = message ?? '';
  const count = Array.from(text).length;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-col gap-1.5">
        {MODE_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={cn(
              'flex items-start gap-2 rounded-md border p-2.5 transition-colors',
              mode === option.value ? 'border-primary/50 bg-primary/5' : 'border-border',
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-foreground/20',
            )}
          >
            <input
              type="radio"
              name={radioName}
              value={option.value}
              checked={mode === option.value}
              disabled={disabled}
              onChange={() => onChange({ mode: option.value })}
              className="mt-0.5 accent-primary"
            />
            <span className="min-w-0">
              <span className="block text-[13px] font-medium text-foreground">{option.label}</span>
              <span className="block text-[11.5px] text-muted-foreground">{option.hint}</span>
            </span>
          </label>
        ))}
        {errors.mode ? <p className="text-[11px] text-destructive">{errors.mode}</p> : null}
      </div>
      <div className="flex flex-col gap-1.5">
        <Textarea
          value={text}
          rows={5}
          disabled={disabled}
          placeholder={messagePlaceholder}
          aria-label="Out-of-hours message"
          onChange={(event) => onChange({ message: event.target.value })}
          className={cn('font-mono text-sm', errors.message && 'border-destructive')}
        />
        <div className="flex items-start justify-between gap-3">
          <PlaceholderChips placeholders={placeholders} />
          <span
            className={cn(
              'shrink-0 text-[11px] tabular-nums',
              count > MESSAGE_MAX ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {count}/{MESSAGE_MAX}
          </span>
        </div>
        {errors.message ? <p className="text-[11px] text-destructive">{errors.message}</p> : null}
      </div>
    </div>
  );
}
